// Dependencies follow the module tree, never similarly named sibling modules.
export function getModuleActions(module) {
  return [...(module.actions || []), ...(module.children || []).flatMap(getModuleActions)];
}

export function resolvePermissionChanges(modules, updates) {
  const entries = new Map();
  const actionType = (action) => String(action.action_name || action.slug?.split(/[_-]/).pop() || '').trim().toLowerCase();
  const visit = (nodes, ancestors = []) => nodes.forEach((module) => {
    const actions = module.actions || [];
    actions.forEach((action) => {
      const type = actionType(action);
      const dependencies = new Set();
      // Any operation requires view access in its own module and its ancestors.
      [module, ...ancestors].forEach((node) => (node.actions || []).forEach((candidate) => {
        if (String(candidate.id) !== String(action.id) &&
            (actionType(candidate) === 'view' || (node !== module && actionType(candidate) === type))) {
          dependencies.add(String(candidate.id));
        }
      }));
      entries.set(String(action.id), { action, dependencies });
    });
    visit(module.children || [], [...ancestors, module]);
  });
  visit(modules || []);
  const values = new Map([...entries].map(([id, { action }]) => [id, Number(action.is_granted) === 1 ? 1 : 0]));
  const disabled = new Set();
  updates.forEach(({ permission_id, is_granted }) => {
    const id = String(permission_id);
    if (!entries.has(id)) throw new Error('Unknown permission');
    values.set(id, Number(is_granted) === 1 ? 1 : 0);
    if (Number(is_granted) !== 1) disabled.add(id);
  });
  // Explicitly revoked prerequisites take precedence over existing dependent grants.
  let changed = true;
  while (changed) {
    changed = false;
    for (const [id, { dependencies }] of entries) {
      if (!disabled.has(id) && [...dependencies].some((dependency) => disabled.has(dependency))) {
        disabled.add(id);
        values.set(id, 0);
        changed = true;
      }
    }
  }
  const enable = (id, visited = new Set()) => {
    if (disabled.has(id) || visited.has(id)) return;
    visited.add(id);
    values.set(id, 1);
    entries.get(id).dependencies.forEach((dependency) => enable(dependency, visited));
  };
  values.forEach((value, id) => { if (value === 1) enable(id); });
  return [...entries].filter(([id, { action }]) => values.get(id) !== (Number(action.is_granted) === 1 ? 1 : 0))
    .map(([id, { action }]) => ({ permission_id: action.id, is_granted: values.get(id) }));
}

export function applyPermissionChanges(modules, changes) {
  const values = new Map(changes.map((change) => [String(change.permission_id), change.is_granted]));
  return (modules || []).map((module) => ({
    ...module,
    actions: (module.actions || []).map((action) => values.has(String(action.id))
      ? { ...action, is_granted: values.get(String(action.id)) } : action),
    children: applyPermissionChanges(module.children, changes),
  }));
}
